/**
 * Bundle 83 Task 2c - Public /stories page.
 *
 * Pulls from `success_story_publications` (Bundle 80). Full-page hero
 * per story: quote, per-participant attribution string (server-side
 * most-private of the pair), event title + short date if permitted.
 * NO portraits unless per-participant photo_permission granted, and
 * even then we intentionally do not render one on this page (Bundle
 * 76 rule: no photos on testimonial surfaces).
 *
 * Auto-hides when zero published stories exist.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const BASE = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BASE}/api` });

function formatShortDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short', year: 'numeric',
    });
  } catch (_) { return null; }
}

const StoryPanel = ({ s }) => {
  const dateLabel = formatShortDate(s.event_date);
  return (
    <article
      className="border-t border-neutral-200 py-16"
      data-testid={`story-${s.id}`}
    >
      <div className="max-w-3xl mx-auto px-4">
        <div className="text-secondary-600 font-display text-6xl leading-none">&ldquo;</div>
        <blockquote
          className="font-display text-2xl sm:text-3xl leading-relaxed text-primary-900 italic mt-2"
          data-testid={`story-quote-${s.id}`}
        >
          {s.quote_text_final}
        </blockquote>
        <div className="mt-8 flex flex-wrap items-center gap-3 text-sm">
          <span className="text-primary-800 font-medium" data-testid={`story-attribution-${s.id}`}>
            {(s.participants_display || [])
              .map((p) => p.attribution_display)
              .join(' and ')}
          </span>
          {s.event_title ? (
            <span className="text-neutral-500">{s.event_title}</span>
          ) : null}
          {dateLabel ? <span className="text-neutral-500">{dateLabel}</span> : null}
          {s.event_format ? (
            <Badge variant="outline" className="text-[10px]">{s.event_format}</Badge>
          ) : null}
        </div>
      </div>
    </article>
  );
};

const StoriesPage = () => {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initial, setInitial] = useState(true);

  const load = useCallback(async (reset = true, curVal = null) => {
    setLoading(true);
    try {
      const params = { limit: 12 };
      if (!reset && curVal) params.cursor = curVal;
      const { data } = await api.get('/stories', { params });
      const fetched = data?.stories || [];
      setItems((prev) => (reset ? fetched : [...prev, ...fetched]));
      setCursor(data?.next_cursor || null);
      setHasMore(!!data?.has_more);
    } catch (_) {
      // Non-blocking; page just shows less.
    } finally {
      setLoading(false);
      setInitial(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  // Auto-hide when zero published stories exist.
  if (!initial && !loading && items.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4" data-testid="stories-empty">
        <div className="text-center max-w-md">
          <div className="text-[11px] uppercase tracking-widest text-secondary-800">Coming soon</div>
          <h1 className="font-display text-3xl font-bold text-primary-900 mt-2">
            Our first published stories will land here.
          </h1>
          <p className="text-neutral-600 mt-3 text-sm">
            When two guests decide to share how their Dostination introduction
            evolved, their story lives here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="stories-page">
      <section className="max-w-3xl mx-auto px-4 pt-20 pb-6">
        <div className="text-[11px] uppercase tracking-widest text-secondary-800">
          Stories, in their own words
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-primary-900 mt-3">
          What can start with a single dinner.
        </h1>
        <p className="text-neutral-700 mt-4 leading-relaxed">
          Every story below is co-authored by the two guests it describes,
          approved word for word, and shared with the attribution they
          personally chose.
        </p>
      </section>

      {items.map((s) => <StoryPanel key={s.id} s={s} />)}

      {hasMore ? (
        <div className="flex justify-center py-12">
          <Button
            variant="outline"
            onClick={() => load(false, cursor)}
            disabled={loading}
            data-testid="stories-load-more-btn"
          >
            {loading ? 'Loading...' : 'Read more stories'}
          </Button>
        </div>
      ) : null}

      <footer className="border-t border-neutral-200 py-10 text-center text-xs text-neutral-500">
        Every story is co-approved. Either guest can revoke at any time and
        the story disappears from this page within minutes.
        <Badge variant="outline" className="ml-2">Bundle 83</Badge>
      </footer>
    </div>
  );
};

export default StoriesPage;
